import * as React from "react";
import {useState} from "react";
import makeStyles from '@mui/styles/makeStyles';
import {Theme, useMediaQuery} from "@mui/material";
import clsx from "clsx";
import TopBar from "./layout/TopBar";
import Sidebar from "./layout/SideBar";

import {Route, Routes, useLocation} from "react-router-dom";
import {HomePage} from "./pages/home/HomePage";
import {useRecoilValue} from "recoil";
import {Hierarchy} from "./Hierarchy";
import {CurrentUserIsShop, CurrentUserState} from "./atoms/CurrentUser";
import {BrandHousePage} from "./pages/brandhouse/BrandHousePage";
import {ShopsPage} from "./pages/brandhouse/ShopsPage";
import {BrandPage} from "./pages/brand/BrandPage";
import {DTCPage} from "./pages/shop/dtc/main/DTCPage";
import {DTCViewPage} from "./pages/shop/dtc/view/DTCViewPage";
import {DTCEditPage} from "./pages/shop/dtc/edit/DTCEditPage";
import {TradeInsPage} from "./pages/shop/tradeins/TradeInsPage";
import {TradeInPage} from "./pages/shop/tradeins/TradeInPage";
import {StockItemsPage} from "./pages/shop/stockitems/StockItemsPage";
import {StockItemEditPage} from "./pages/shop/stockitems/edit/StockItemEditPage";
import {ConsumerPage} from "./pages/shop/shopdata/consumers/ConsumerPage";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import {ShippingCarrierEdit} from "./pages/admin/shippingcarriers/ShippingCarrierEdit";
import {ShippingCarriers} from "./pages/admin/shippingcarriers/ShippingCarriers";
import {DashboardPage} from "./pages/brand/DashboardPage";
import {NFCPage} from "./pages/logistics/nfc/NFCPage";
import {InventoryPage} from "./pages/logistics/inventory/InventoryPage";
import {LogisticsTrackingPage} from "./pages/logistics/LogisticsTrackingPage";
import {LogisticsOrdersPage} from "./pages/logistics/LogisticsOrdersPage";
import {InvoicesPage} from "./pages/brandhouse/invoices/InvoicesPage";
import {LogisticsStockItemsPage} from "./pages/logistics/LogisticsStockItemsPage";
import {BrandSettingsPage} from "./pages/settings/BrandSettingsPage";
import {LogisticsInvoicesPage} from "./pages/logistics/LogisticsInvoicesPage";
import {InviteAcceptPage} from "./pages/account/InviteAcceptPage";
import {BrandItemsPageWrapper} from "./pages/brand/BrandItemsPage";
import {BrandItemCreateEmptyPage, BrandItemCreatePage, BrandItemPage} from "./pages/brand/brandItem/BrandItemPage";
import {BrandTradeInsListPage} from "./pages/brand/tradeins/BrandTradeInsListPage";
import {LogisticsTradeInsListPage} from "./pages/logistics/tradeins/LogisticsTradeInsListPage";
import {LogisticsConsumersListPage} from "./pages/logistics/consumers/LogisticsConsumersListPage";
import {BrandTrackingPage} from "./pages/brand/BrandTrackingPage";
import {AuditNotesPage} from "./pages/brandhouse/audits/AuditNotesPage";
import {AuditNoteDetailsPage} from "./pages/brandhouse/audits/AuditNoteDetailsPage";
import {ImageAuditPage} from "./pages/admin/imageaudit/ImageAuditPage";
import {BrandOrdersPage} from "./pages/brand/BrandOrdersPage";
import {BrandMessagesPage} from "./pages/brand/BrandMessagesPage";
import {OrderPage} from "./pages/shop/orders/OrderPage";
import {BrandTradeInPage} from "./pages/brand/tradeins/BrandTradeInPage";
import {ConnectorsPage} from "./pages/admin/ConnectorsPage";
import {ConnectorCreatePage, ConnectorEditPage} from "./pages/admin/ConnectorEditPage";
import {LogisticsBrandItemsPageWrapper, LogisticsItemPage} from "./pages/logistics/LogisticsBrandItemsPage";
import {LogisticsItemIssuesPage} from "./pages/logistics/LogisticsItemIssuesPage";
import {LogisticsIntegrationsPage} from "./pages/logistics/LogisticsIntegrationsPage";
import {LogisticsIntegrationPage} from "./pages/logistics/LogisticsIntegrationPage";
import {MessagesPage} from "./pages/admin/MessagesPage";
import {SystemMessagesPage} from "./pages/admin/SystemMessagePage";
import {BrandParcelsPage} from "./pages/brand/BrandParcelsPage";
import {AuditPage} from "./pages/admin/audit/AuditPage";
import {VouchersPage} from "./pages/brand/vouchers/VouchersPage";
import {BrandConsumersPage} from "./pages/brand/consumers/BrandConsumersPage";
import {VoucherPage} from "./pages/brand/vouchers/VoucherPage";
import {MasterDataPage} from "./pages/brand/masterdata/MasterDataPage";
import {MasterDataEditPage} from "./pages/brand/masterdata/MasterDataEditPage";
import {ShippingCostsPage} from "./pages/shippingcosts/ShippingCostsPage";
import {MasMigrationRunPage} from "./pages/admin/masmigration/MasMigrationRunPage";
import {LogisticsPartnersPage} from "./pages/logistics/LogisticsPartnersPage";
import {LogisticsPartnerSettingsPage} from "./pages/logistics/LogisticsPartnerSettingsPage";

const useStyles = makeStyles((theme: Theme) => ({
   root: {
      height: 'calc(100vh - 50px)',
      paddingRight: '15px'
   },
   rootUser: {
      paddingTop: 48,
   },
   topBar: {
      backgroundColor: 'white'
   },
   anchorBrandHouse: {
      position: 'fixed',
      right: 0,
      top: 0,
      bottom: 0,
      paddingTop: '20px',
      paddingLeft: '4px',
      paddingRight: '2px',
      backgroundColor: 'white',
      borderLeft: '1px solid #e0e0e0',
      borderBottom: '1px solid #e0e0e0',
      cursor: 'pointer'
   },
   shiftContentRight: {
      paddingRight: 320
   },
   shiftContentLeft: {
      paddingLeft: 320
   },
   content: {
      height: '100%'
   },
   flexGrow: {
      flexGrow: 1
   },
   signOutButton: {
      marginLeft: theme.spacing(1)
   },
   treeRoot: {
      height: 264,
      flexGrow: 1,
      maxWidth: 400,
   }
}));

export const isInsideIframe = () => {
   try {
      return window.self !== window.top;
   } catch (e) {
      return true;
   }
}


const MainWindow = () => {
   const currentUser = useRecoilValue(CurrentUserState);
   const isBrandHouse = useRecoilValue(CurrentUserIsShop);
   const location = useLocation();

   // Hide frame for specific routes
   const hideFrameRoutes = ['/shippingcosts'];
   const shouldHideFrame = hideFrameRoutes.includes(location.pathname);

   // Responsiveness
   const classes = useStyles();
   const isDesktop = useMediaQuery('(min-width:1200px)');

   // Dynamic page stuff
   const [openSidebar, setOpenSidebar] = useState(false);
   const handleSidebarOpen = () => {
      setOpenSidebar(true);
   };
   const handleSidebarClose = () => {
      setOpenSidebar(false);
   };

   const shouldOpenSidebar = isDesktop ? true : openSidebar;

   const isInsideIFrame = isInsideIframe();

   const showTopBar = !isBrandHouse || !isInsideIFrame;

   return (
      <div
         className={clsx({
            [classes.root]: true,
            [classes.rootUser]: showTopBar,
            [classes.shiftContentLeft]: (showTopBar && shouldOpenSidebar && !shouldHideFrame),
            [classes.shiftContentRight]: (!showTopBar && shouldOpenSidebar && !shouldHideFrame)
         })}
      >
         {!currentUser?.hideFrame && <>
            {showTopBar &&
               <TopBar onSidebarOpen={handleSidebarOpen} anchor={showTopBar ? "left" : "right"} hideUserWidgets={shouldHideFrame}/>
            }
            {!showTopBar && !shouldHideFrame &&
               <div onClick={handleSidebarOpen} className={classes.anchorBrandHouse}><AccountBalanceIcon
                  htmlColor={"#c0c0c0"}
                  fontSize={"small"}/>
               </div>
            }
            {!shouldHideFrame &&
               <Sidebar onClose={handleSidebarClose}
                        open={shouldOpenSidebar}
                        variant={isDesktop ? 'persistent' : 'temporary'}
                        anchor={showTopBar ? "left" : "right"}
                        topbarHidden={!showTopBar}
               >
                  <Hierarchy/>
               </Sidebar>
            }
         </>}
         <main>
            <div className={classes.content}>
               <Routes>
                  <Route path={"/"} element={<HomePage/>}/>
                  <Route path={"/account/accept/:joinId"} element={<InviteAcceptPage/>}/>
                  <Route path={"/account/accept"} element={<InviteAcceptPage/>}/>
                  <Route path={"/admin/connectors/create"} element={<ConnectorCreatePage/>}/>
                  <Route path={"/admin/connectors/:connectorId"} element={<ConnectorEditPage/>}/>
                  <Route path={"/admin/connectors"} element={<ConnectorsPage/>}/>
                  <Route path={"/admin/messages"} element={<MessagesPage/>}/>
                  <Route path={"/admin/systemmessages"} element={<SystemMessagesPage/>}/>
                  <Route path={"/admin/shippingcarriers"} element={<ShippingCarriers/>}/>
                  <Route path={"/admin/shippingcarriers/:shippingCarrierId"}
                         element={<ShippingCarrierEdit/>}/>
                  <Route path={"/admin/tracking"} element={<LogisticsTrackingPage/>}/>
                  <Route path={"/admin/image-audit"} element={<ImageAuditPage/>}/>
                  <Route path={"/admin/audit"} element={<AuditPage/>}/>
                  <Route path={"/admin/mas-migration/run/:runId"} element={<MasMigrationRunPage/>}/>
                  <Route path={"/shippingcosts"} element={<ShippingCostsPage/>}/>
                  <Route path={"/logisticspartners"} element={<LogisticsPartnersPage/>}/>
                  <Route path={"/logisticspartners/:logisticsPartnerId/settings"}
                         element={<LogisticsPartnerSettingsPage/>}/>
                  <Route path={"/logisticspartners/:logisticsPartnerId/alltradeins"}
                         element={<LogisticsTradeInsListPage/>}/>
                  <Route path={"/logisticspartners/:logisticsPartnerId/allconsumers"}
                         element={<LogisticsConsumersListPage/>}/>
                  <Route path={"/logisticspartners/:logisticsPartnerId/orders"}
                         element={<LogisticsOrdersPage/>}/>
                  <Route path={"/logisticspartners/:logisticsPartnerId/stockitems"}
                         element={<LogisticsStockItemsPage/>}/>
                  <Route path={"/logisticspartners/:logisticsPartnerId/branditems"}
                         element={<LogisticsBrandItemsPageWrapper/>}>
                     <Route path={":brandItemId"} element={<LogisticsItemPage/>}/>
                  </Route>
                  <Route path={"/logisticspartners/:logisticsPartnerId/invoices"}
                         element={<LogisticsInvoicesPage/>}/>
                  <Route path={"logisticspartners/:logisticsPartnerId/nfctags"}
                         element={<NFCPage/>}/>
                  <Route path={"logisticspartners/:logisticsPartnerId/inventory"}
                         element={<InventoryPage/>}/>
                  <Route path={"logisticspartners/:logisticsPartnerId/integrations/:instanceId"}
                         element={<LogisticsIntegrationPage/>}/>
                  <Route path={"logisticspartners/:logisticsPartnerId/integrations"}
                         element={<LogisticsIntegrationsPage/>}/>
                  <Route path={"/logisticspartners/:logisticsPartnerId/itemissues"} element={<LogisticsItemIssuesPage/>}/>
                  <Route path={"brands"} element={<BrandHousePage/>}/>
                  <Route path={"brands/:brandHouseId"} element={<ShopsPage/>}/>
                  <Route path={"brands/:brandHouseId/settings"} element={<BrandSettingsPage/>}/>
                  <Route path={"brands/:brandHouseId/settings/*"} element={<BrandSettingsPage/>}/>
                  <Route path={"brands/:brandHouseId/invoices"} element={<InvoicesPage/>}/>
                  <Route path={"brands/:brandHouseId/dashboard"} element={<DashboardPage/>}/>
                  <Route path={"brands/:brandHouseId/tradeins/:tradeInId"} element={<BrandTradeInPage/>}/>
                  <Route path={"brands/:brandHouseId/tradeins"} element={<BrandTradeInsListPage/>}/>
                  <Route path={"brands/:brandHouseId/tracking"} element={<BrandTrackingPage/>}/>
                  <Route path={"brands/:brandHouseId/messages"} element={<BrandMessagesPage/>}/>
                  <Route path={"brands/:brandHouseId/parcels"} element={<BrandParcelsPage/>}/>
                  <Route path={"brands/:brandHouseId/audits/:auditNoteId"} element={<AuditNoteDetailsPage/>}/>
                  <Route path={"brands/:brandHouseId/audits"} element={<AuditNotesPage/>}/>
                  <Route path={"brands/:brandHouseId/orders"} element={<BrandOrdersPage/>}/>
                  <Route path={"brands/:brandHouseId/orders/:shopId/:orderId"}
                         element={<OrderPage/>}/>
                  <Route path={"brands/:brandHouseId/masterdata/create/:groupId"} element={<MasterDataEditPage/>}/>
                  <Route path={"brands/:brandHouseId/masterdata/:editId"} element={<MasterDataEditPage/>}/>
                  <Route path={"brands/:brandHouseId/masterdata"} element={<MasterDataPage/>}/>
                  <Route path={"brands/:brandHouseId/stock/create/:brandProductSizeId"}
                         element={<BrandItemCreatePage/>}/>
                  <Route path={"brands/:brandHouseId/stock/create"} element={<BrandItemCreateEmptyPage/>}/>
                  <Route path={"brands/:brandHouseId/stock"} element={<BrandItemsPageWrapper/>}>
                     <Route path={":brandItemId"} element={<BrandItemPage inDrawer={true}/>}/>
                  </Route>
                  <Route path={"brands/:brandHouseId/stock/edit/:brandItemId"}
                         element={<BrandItemPage inDrawer={false}/>}/>

                  <Route path={"brands/:brandHouseId/rewards"}
                         element={<VouchersPage/>}/>
                  <Route path={"brands/:brandHouseId/rewards/:voucherId"}
                         element={<VoucherPage/>}/>

                  <Route path={"brands/:brandHouseId/consumers"}
                         element={<BrandConsumersPage/>}/>
                  <Route path={"brands/:brandHouseId/consumers/:shopId/:consumerId"}
                         element={<ConsumerPage/>}/>

                  <Route path={"brands/:brandHouseId/:shopId"} element={<BrandPage/>}/>
                  <Route path={"brands/:brandHouseId/:shopId/dtc"}
                         element={<DTCPage/>}/>
                  <Route path={"brands/:brandHouseId/:shopId/dtc/:brandRuleId"}
                         element={<DTCViewPage/>}/>
                  <Route path={"brands/:brandHouseId/:shopId/dtc/:brandRuleId/edit"}
                         element={<DTCEditPage/>}/>
                  <Route path={"brands/:brandHouseId/:shopId/tradeins"}
                         element={<TradeInsPage/>}/>
                  <Route path={"brands/:brandHouseId/:shopId/tradeins/:tradeInId"}
                         element={<TradeInPage/>}/>
                  <Route path={"brands/:brandHouseId/:shopId/stock"}
                         element={<StockItemsPage/>}/>
                  <Route path={"brands/:brandHouseId/:shopId/stock/:stockItemId/edit"}
                         element={<StockItemEditPage/>}/>
               </Routes>
            </div>
         </main>
      </div>
   );
};

export default MainWindow;
